"use client";

import { useEffect, useState, useCallback } from "react";
import { Theme } from "./ThemeProvider";
import { ThemeContext } from "./ThemeContext";

const getSystemTheme = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export default function ThemeClientProvider({
  children,
  initialTheme = "system",
}: {
  children: React.ReactNode;
  initialTheme?: Theme;
}) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  // Initialize theme from localStorage on mount (client-side only)
  useEffect(() => {
    try {
      const stored = localStorage.getItem("persist:theme");
      if (stored) {
        const parsed = JSON.parse(stored);
        let resolvedTheme: Theme = "system";
        
        if (parsed.darkMode === "true") resolvedTheme = "dark";
        else if (parsed.darkMode === "false") resolvedTheme = "light";
        
        if (resolvedTheme !== initialTheme) {
          setThemeState(resolvedTheme);
        }
      }
    } catch (e) {
      console.error("Failed to parse theme from local storage", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      // Persist to localStorage
      const stored = localStorage.getItem("persist:theme");
      const parsed = stored ? JSON.parse(stored) : {};
      
      if (newTheme === "dark") {
        parsed.darkMode = "true";
      } else if (newTheme === "light") {
        parsed.darkMode = "false";
      } else {
        delete parsed.darkMode; 
      }
      
      localStorage.setItem("persist:theme", JSON.stringify(parsed));
      
      // Persist to cookie for SSR consistency
      document.cookie = `theme=${newTheme}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (e) {
      console.error("Failed to save theme to local storage or cookie", e);
    }
  }, []);

  useEffect(() => {
    const applyTheme = (t: Theme) => {
      const resolved = t === "system" ? getSystemTheme() : t;
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(resolved);
    };

    applyTheme(theme);

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
